import {
  Component,
  ChangeDetectionStrategy,
  HostBinding,
  OnInit,
  signal,
  input,
  computed,
  effect,
  inject,
  PLATFORM_ID,
} from '@angular/core';

import { IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';
import { NgClass, NgStyle, NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, skip } from 'rxjs';
import { ImageLoaderParams, ImageSize } from '../../image.types';

const errorImage = 'https://cdn.pedix.app/no-product-image.png?alt=media&size=600';
/**
 * Image component with lazy loading using Angular's NgOptimizedImage directive.
 * Supports fill mode for flexible sizing and explicit dimensions when needed.
 */
@Component({
  selector: 'pxw-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, IconLoadingComponent, NgStyle, NgOptimizedImage],
  host: {
    '[style.width.px]': 'frameWidth()',
    '[style.height.px]': 'frameHeight()',
  },
})
export class ImageComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);

  readonly src = input.required<string | null>();
  /**
   * Size of the default image to request from the CDN
   */
  readonly imageSize = input.required<ImageSize>();
  readonly priority = input(false);
  readonly lazyLoad = input(true);
  readonly loaderParams = input<Partial<ImageLoaderParams> | undefined>();

  /**
   * NOTE: you need to either provide "width", and "height" or "aspectRatio" for fixed-size images
   * or provide "sizes" for responsive images in "fill mode"
   */
  readonly width = input<number | null>(null);
  readonly height = input<number | null>(null);
  readonly aspectRatio = input<number | null>(null);
  readonly sizes = input<string | undefined>();
  //--------------------------------

  readonly frameWidth = input<number | null>(null);
  readonly frameHeight = input<number | null>(null);

  readonly objectFit = input<'fill' | 'contain' | 'cover' | 'scale-down' | 'none'>('contain');

  readonly alt = input('');
  readonly loader = input<'sm' | 'lg' | 'none'>('lg');

  readonly isLoading = signal(false);
  readonly hasError = signal(false);

  readonly imageUrl = computed<string>(() => {
    const src = this.src();

    if (this.hasError() || !src) {
      return errorImage;
    }

    return src;
  });
  readonly isHttpResource = computed(() => this.imageUrl().startsWith('http'));

  /**
   * Computed dimensions for the img tag.
   * If width and height are provided, use them.
   * If width and aspectRatio are provided, calculate height.
   */
  readonly imgDimensions = computed(() => {
    const w = this.width();
    const h = this.height();
    const ar = this.aspectRatio();

    if (w && h) {
      return { width: w, height: h, fill: false };
    }

    if (w && ar) {
      return { width: w, height: Math.round(w / ar), fill: false };
    }

    return { width: undefined, height: undefined, fill: true };
  });

  readonly hasExplicitDimensions = computed(() => !this.imgDimensions().fill);
  readonly mergedLoaderParams = computed<ImageLoaderParams>(() => ({
    ...(this.loaderParams() || {}),
    imageSize: this.imageSize(),
  }));
  readonly loadingStrategy = computed(() => {
    if (this.priority()) {
      return undefined;
    }
    if (this.lazyLoad()) {
      return 'lazy';
    }
    return 'eager';
  });

  constructor() {
    effect(() => {
      const hasExplicitDimensions = this.hasExplicitDimensions();

      if (!hasExplicitDimensions && !this.sizes()) {
        const errorMessage =
          '[pxw-image]: must provide either explicit dimensions ("width" and "height" or "aspectRatio") or "sizes" property';

        if (isPlatformBrowser(this.platformId)) {
          throw new Error(errorMessage);
        } else {
          console.error(errorMessage);
        }
      }
    });
    toObservable(this.src)
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe(() => {
        this.isLoading.set(true);
        this.hasError.set(false);
      });
  }

  ngOnInit(): void {
    if (this.lazyLoad()) {
      this.isLoading.set(true);
    }
  }

  onImageLoad() {
    this.isLoading.set(false);
  }

  onImageError() {
    this.isLoading.set(true);
    this.hasError.set(true);
  }
}
